import React, { useState, useEffect, useContext } from "react";
import {
  Box, Button, CircularProgress, Typography, IconButton,
  Tooltip, Chip, useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DetailsCard from "./DetailsCard";
import SourcesModal from "./SourcesModal";
import { useDispatch, useSelector } from "react-redux";
import { setHighlightError } from "../../../../redux/actions/contractAction";
import EditorContext from "../../../../contexts/EditorContext";
import { useTranslation } from "react-i18next";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

const RiskAnalysis = ({ contractId, startRiskAnalysis }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const risks = useSelector((state) => state.contract.risks || []);
  const loadingRisk = useSelector((state) => state.contract.loadingRisk);
  const [expandedRiskIndex, setExpandedRiskIndex] = useState(null);
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);
  const [selectedRiskSources, setSelectedRiskSources] = useState([]);
  const isRTL = i18n.language === 'ar';
  const editorRef = useContext(EditorContext);

  const RISK_CONFIG = {
    "High Risk":   { color: theme.palette.error.main,   bg: alpha(theme.palette.error.main, 0.08),   icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} /> },
    "Medium Risk": { color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.08), icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} /> },
    "Low Risk":    { color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.08), icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  };

  const formatHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<br\s*\/?>/gi, ' • ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ').trim();
  };

  const clearHighlights = () => {
    const editor = editorRef?.current?.getEditor();
    if (editor?.commands?.clearRiskHighlight) editor.commands.clearRiskHighlight();
  };

  const scrollToHighlight = () => {
    setTimeout(() => {
      const els = document.querySelectorAll('.risk-highlight');
      if (els.length > 0) els[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleExtractRisks = async () => {
    if (!contractId) return;
    clearHighlights();
    ReactGA.event({ category: "RiskAnalysis", action: "Click Extract Risks", label: hasRisks ? "Re-extract" : "Initial Extract" });
    tracker.event({ category: "RiskAnalysis", action: "Click Extract Risks", label: hasRisks ? "Re-extract" : "Initial Extract" });
    await startRiskAnalysis();
  };

  const handleRiskClick = (risk, index) => {
    ReactGA.event({ category: 'RiskAnalysis', action: 'Expand Risk Card', label: `${risk.level}` });
    tracker.event({ category: 'RiskAnalysis', action: 'Expand Risk Card', label: `${risk.level}` });
    const wasExpanded = expandedRiskIndex === index;
    setExpandedRiskIndex(wasExpanded ? null : index);
    const editor = editorRef?.current?.getEditor();
    if (editor) {
      clearHighlights();
      if (!wasExpanded && risk.erreur) {
        try {
          const riskColor = RISK_CONFIG[risk.level]?.color;
          editor.commands.highlightRiskText(risk.erreur, riskColor);
          scrollToHighlight();
        } catch (err) {
          console.error("Error applying highlight:", err);
        }
      }
    }
  };

  useEffect(() => () => clearHighlights(), [editorRef]);

  const hasRisks = risks && risks.length > 0;

  const riskCounts = risks.reduce((acc, r) => {
    acc[r.level] = (acc[r.level] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ width: "100%", height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Chip
          label={t("riskAnalysis.Chip")}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 0.75,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {t("riskAnalysis.title")}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mt: 0.25 }}>
              {t("riskAnalysis.subtitle")}
            </Typography>
          </Box>
          <Tooltip title={hasRisks ? t("riskAnalysis.reExtractRisks") : t("riskAnalysis.extractRisks")}>
            <span>
              <IconButton
                aria-label={hasRisks ? t("riskAnalysis.reExtractRisks") : t("riskAnalysis.extractRisks")}
                onClick={handleExtractRisks}
                disabled={loadingRisk}
                size="small"
                sx={{
                  mt: 0.25,
                  width: 30, height: 30,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  borderRadius: '8px',
                  color: theme.palette.text.secondary,
                  transition: 'all 0.15s',
                  '&:hover': { color: theme.palette.text.primary, borderColor: alpha(theme.palette.text.secondary, 0.5) },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                {loadingRisk ? <CircularProgress size={13} color="inherit" /> : hasRisks ? <RefreshIcon sx={{ fontSize: 15 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Risk summary chips ── */}
      {hasRisks && !loadingRisk && (
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexShrink: 0 }}>
          {Object.entries(riskCounts).map(([level, count]) => {
            const cfg = RISK_CONFIG[level];
            if (!cfg) return null;
            return (
              <Box key={level} sx={{
                display: 'flex', alignItems: 'center', gap: '4px',
                px: 1, py: 0.4, borderRadius: '6px',
                backgroundColor: cfg.bg,
                border: `1px solid ${alpha(cfg.color, 0.2)}`,
                color: cfg.color, fontSize: '0.72rem', fontWeight: 600,
              }}>
                {cfg.icon}
                {count}
              </Box>
            );
          })}
          <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.disabled, alignSelf: 'center', ml: 0.5 }}>
            {risks.length} {t('riskAnalysis.risks', 'risques')}
          </Typography>
        </Box>
      )}

      {/* ── Risks list ── */}
      {hasRisks ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {risks.map((risk, index) => {
            const cfg = RISK_CONFIG[risk.level] || RISK_CONFIG["Low Risk"];
            const isExpanded = expandedRiskIndex === index;
            const hasSources = Array.isArray(risk.sources) && risk.sources.length > 0;
            const desc = formatHtml(risk.erreur);

            return (
              <React.Fragment key={index}>
                {/* Risk card */}
                <Box
                  onClick={() => handleRiskClick(risk, index)}
                  sx={{
                    borderRadius: '10px',
                    border: `1px solid ${isExpanded ? alpha(cfg.color, 0.4) : alpha(theme.palette.divider, 0.6)}`,
                    backgroundColor: isExpanded ? cfg.bg : (isDark ? alpha('#fff', 0.02) : '#fff'),
                    cursor: 'pointer',
                    transition: 'all 0.16s ease',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: alpha(cfg.color, 0.35),
                      backgroundColor: cfg.bg,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25 }}>
                    {/* Color accent bar */}
                    <Box sx={{ width: 3, borderRadius: '2px', backgroundColor: cfg.color, alignSelf: 'stretch', flexShrink: 0, minHeight: 36 }} />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: cfg.color }}>
                          {cfg.icon}
                          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: cfg.color }}>
                            {risk.level}
                          </Typography>
                        </Box>
                        {hasSources && (
                          <Tooltip title={t("riskAnalysis.sources.viewSources", "Voir les sources")}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRiskSources(risk.sources || []);
                                setSourcesModalOpen(true);
                              }}
                              sx={{
                                width: 24, height: 24, borderRadius: '6px',
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
                              }}
                            >
                              <BookmarkBorderIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.8rem', lineHeight: 1.55, color: theme.palette.text.primary, textAlign: 'left',
                          display: '-webkit-box', WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: isExpanded ? 'none' : 2, overflow: 'hidden',
                        }}
                      >
                        {desc}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Expanded details */}
                {isExpanded && (
                  <DetailsCard
                    details={risk.details}
                    suggestion={risk.suggestion}
                    errorText={risk.erreur}
                    accentColor={cfg.color}
                    onClose={() => { setExpandedRiskIndex(null); clearHighlights(); }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      ) : loadingRisk ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
          <CircularProgress size={24} />
          <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
            {t("riskAnalysis.analyzing", "Analyzing contract risks…")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            backgroundColor: alpha(theme.palette.text.secondary, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 24, color: theme.palette.text.disabled }} />
          </Box>
          <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
            {t("riskAnalysis.extractClauses")}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={handleExtractRisks}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
          >
            {t("riskAnalysis.extractRisks")}
          </Button>
        </Box>
      )}

      <SourcesModal
        open={sourcesModalOpen}
        onClose={() => setSourcesModalOpen(false)}
        sources={selectedRiskSources}
      />
    </Box>
  );
};

export default RiskAnalysis;
